/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MemberBadge from "../components/MemberBadge";

/**
 * Session 57 · Founding Member first-login flow.
 *
 * A four-step onboarding designed to complete in under two minutes:
 *   1. Create new password        (replaces the temp password from invite)
 *   2. Complete profile           (display name + city + title/company/bio)
 *   3. Accept Terms + Privacy     (required to enter the platform)
 *   4. Welcome to Métier tour     (3 dots: The Network / The Commons / Métier Hours)
 *
 * On completion → redirects to /spaces (workspaces directory).
 */
export default function FirstLogin() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [city, setCity] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [bio, setBio] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [legal, setLegal] = useState({ terms_version: null, privacy_version: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    if (!user.must_change_password) {
      // Nothing to do — punt back to the app.
      nav("/spaces", { replace: true });
      return;
    }
    setDisplayName(user.display_name || user.name || "");
    setCity(user.city || "");
    setTitle(user.title || "");
    setCompany(user.company || "");
    setBio(user.bio || "");
  }, [user, nav]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/legal/current");
        const docs = r.data?.documents || {};
        setLegal({
          terms_version: docs?.terms?.version,
          privacy_version: docs?.privacy?.version,
        });
      } catch { /* non-blocking */ }
    })();
  }, []);

  if (!user) return null;

  const stepValid = () => {
    if (step === 1) {
      return pw.length >= 8 && pw === pw2;
    }
    if (step === 2) {
      return displayName.trim().length > 1 && city.trim().length > 1 && (title.trim() || bio.trim());
    }
    if (step === 3) {
      return acceptTerms && legal.terms_version && legal.privacy_version;
    }
    return true;
  };

  const submitAll = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post("/auth/first-login/complete", {
        new_password: pw,
        display_name: displayName.trim() || undefined,
        city: city.trim() || undefined,
        title: title.trim() || undefined,
        company: company.trim() || undefined,
        bio: bio.trim() || undefined,
        accept_terms: true,
        terms_version: legal.terms_version,
        privacy_version: legal.privacy_version,
      });
      await refresh();
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const finishTour = () => {
    nav("/spaces", { replace: true });
  };

  return (
    <div
      data-testid="first-login-page"
      style={{
        minHeight: "100vh",
        background: "#FAF8F3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        fontFamily: "Helvetica, Arial, sans-serif",
        color: "#1B2340",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(27,35,64,0.08)",
          boxShadow: "0 20px 60px rgba(27,35,64,0.08)",
          padding: "36px 40px 32px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700 }}>MÉTIER</div>
          <MemberBadge member_type="founding_member" size="sm" />
        </div>

        {/* Progress */}
        <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 4,
                background: step >= n ? "#B8913A" : "rgba(27,35,64,0.1)",
                transition: "background 0.25s ease",
              }}
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 30, margin: "0 0 6px" }}>
              Welcome, {(user.display_name || user.name || "friend").split(" ")[0]}.
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.7)", margin: "0 0 22px" }}>
              First — let&#39;s replace that temporary password with something only you know.
            </p>
            <label style={label}>New password</label>
            <input
              data-testid="first-login-new-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              style={input}
              autoFocus
            />
            <label style={{ ...label, marginTop: 14 }}>Confirm password</label>
            <input
              data-testid="first-login-confirm-password"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Type it once more"
              style={input}
            />
            {pw && pw2 && pw !== pw2 && (
              <p style={hint("#B44")}>Passwords don&#39;t match yet.</p>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 30, margin: "0 0 6px" }}>
              Tell us a little about you.
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.7)", margin: "0 0 22px" }}>
              This is what other members see. Keep it short — you can edit anytime.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={label}>Display name *</label>
                <input data-testid="first-login-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={input} />
              </div>
              <div>
                <label style={label}>City *</label>
                <input data-testid="first-login-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Portsmouth, NH" style={input} />
              </div>
              <div>
                <label style={label}>Role / title</label>
                <input data-testid="first-login-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Designer, Founder, Attorney…" style={input} />
              </div>
              <div>
                <label style={label}>Company</label>
                <input data-testid="first-login-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Optional" style={input} />
              </div>
            </div>
            <label style={{ ...label, marginTop: 14 }}>Short bio</label>
            <textarea
              data-testid="first-login-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              placeholder="One sentence about what you're working on."
              style={{ ...input, resize: "vertical", minHeight: 72 }}
            />
          </>
        )}

        {step === 3 && (
          <>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 30, margin: "0 0 6px" }}>
              The house rules.
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.7)", margin: "0 0 22px" }}>
              Two short documents — designed to keep the community respectful and your data private.
            </p>
            <div style={{
              background: "#FAF8F3",
              border: "1px solid rgba(27,35,64,0.1)",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, letterSpacing: "0.16em", color: "#B8913A", fontWeight: 600, marginBottom: 4 }}>
                CURRENT VERSIONS
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                <a href="/legal/terms" target="_blank" rel="noreferrer" style={link}>Terms of Service v{legal.terms_version || "…"}</a>
                {" · "}
                <a href="/legal/privacy" target="_blank" rel="noreferrer" style={link}>Privacy Policy v{legal.privacy_version || "…"}</a>
              </div>
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input
                data-testid="first-login-accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.65 }}>
                I&#39;ve read and accept the Terms of Service and Privacy Policy above.
              </span>
            </label>
          </>
        )}

        {step === 4 && (
          <>
            <div style={{ marginBottom: 18 }}>
              <MemberBadge member_type="founding_member" size="md" />
            </div>
            {tourStep === 0 && (
              <>
                <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 30, margin: "0 0 8px" }}>
                  You&#39;re in.
                </h1>
                <p style={{ fontSize: 15, lineHeight: 1.75, color: "rgba(27,35,64,0.8)", margin: "0 0 8px" }}>
                  Your Founding Member badge is now permanent — it appears on your profile, your posts, and every message you send.
                </p>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(27,35,64,0.7)", margin: 0 }}>
                  Three quick things worth knowing before you dive in.
                </p>
              </>
            )}
            {tourStep === 1 && (
              <TourSlide
                eyebrow="THE NETWORK"
                title="Book workspaces from your phone."
                body="Boutique offices, meeting rooms, and event venues — curated, not aggregated. Real people, real spaces, no scraping."
              />
            )}
            {tourStep === 2 && (
              <TourSlide
                eyebrow="THE COMMONS"
                title="Talk shop with the community."
                body="Post about what you're working on, ask for referrals, share a great café. This is where the network becomes real."
              />
            )}
            {tourStep === 3 && (
              <TourSlide
                eyebrow="MÉTIER HOURS"
                title="Café perks around the corner."
                body="Show your Métier profile at partner cafés for member-only perks. Fresh partners added every week."
              />
            )}
          </>
        )}

        {error && <p style={hint("#B44")}>{error}</p>}

        {/* Footer nav */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 26, paddingTop: 18, borderTop: "1px solid rgba(27,35,64,0.06)",
        }}>
          {step < 4 ? (
            <>
              <button
                onClick={async () => { await logout(); nav("/login", { replace: true }); }}
                style={btnGhost}
              >
                Sign out
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                {step > 1 && (
                  <button
                    data-testid="first-login-back"
                    onClick={() => setStep((s) => s - 1)}
                    style={btnSecondary}
                    disabled={saving}
                  >
                    Back
                  </button>
                )}
                {step < 3 && (
                  <button
                    data-testid="first-login-next"
                    onClick={() => stepValid() && setStep((s) => s + 1)}
                    disabled={!stepValid() || saving}
                    style={{ ...btnPrimary, opacity: stepValid() ? 1 : 0.5 }}
                  >
                    Continue
                  </button>
                )}
                {step === 3 && (
                  <button
                    data-testid="first-login-submit"
                    onClick={submitAll}
                    disabled={!stepValid() || saving}
                    style={{ ...btnPrimary, opacity: stepValid() && !saving ? 1 : 0.5 }}
                  >
                    {saving ? "Finishing…" : "Enter Métier"}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => tourStep > 0 && setTourStep((t) => t - 1)} style={btnSecondary} disabled={tourStep === 0}>
                Back
              </button>
              {tourStep < 3 ? (
                <button
                  data-testid="first-login-tour-next"
                  onClick={() => setTourStep((t) => t + 1)}
                  style={btnPrimary}
                >
                  {tourStep === 0 ? "Show me around" : "Next"}
                </button>
              ) : (
                <button data-testid="first-login-finish" onClick={finishTour} style={btnPrimary}>
                  Start exploring →
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TourSlide({ eyebrow, title, body }) {
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.18em", color: "#B8913A", fontWeight: 700, marginBottom: 8 }}>
        {eyebrow}
      </div>
      <h2 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 24, margin: "0 0 10px" }}>{title}</h2>
      <p style={{ fontSize: 15, lineHeight: 1.75, color: "rgba(27,35,64,0.8)", margin: 0 }}>{body}</p>
    </div>
  );
}

// ---------- inline styles ----------
const input = {
  width: "100%",
  padding: "11px 14px",
  border: "1px solid rgba(27,35,64,0.16)",
  borderRadius: 10,
  fontSize: 15,
  fontFamily: "inherit",
  color: "#1B2340",
  background: "#fff",
  boxSizing: "border-box",
};
const label = { display: "block", fontSize: 12, letterSpacing: "0.06em", color: "rgba(27,35,64,0.7)", marginBottom: 6, textTransform: "uppercase", fontWeight: 600 };
const link = { color: "#B8913A", textDecoration: "underline" };
const hint = (color) => ({ color, fontSize: 13, marginTop: 8, marginBottom: 0 });
const btnPrimary = {
  padding: "11px 22px",
  background: "#1B2340",
  color: "#fff",
  border: "none",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  letterSpacing: "0.04em",
};
const btnSecondary = {
  padding: "11px 20px",
  background: "#fff",
  color: "#1B2340",
  border: "1px solid rgba(27,35,64,0.2)",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};
const btnGhost = {
  padding: "10px 4px",
  background: "transparent",
  color: "rgba(27,35,64,0.5)",
  border: "none",
  fontSize: 13,
  cursor: "pointer",
  textDecoration: "underline",
};
